import {
    IOrganisationUnaSettings,
    IOrganisationUnaSettingsInput,
    OrganisationUnaSettings,
    OrganisationUnaSettingsInput
} from '@/api/klip-api.proxy';
import {useForm} from 'vee-validate';
import {EEventType, useSignalrHub} from '@/plugins/signalr';
import {useUserStore} from '@/app/shared/state/UserDataModule';
import {EUserRole} from '@/app/shared/state/user-role';
import {computed, defineComponent, onBeforeUnmount, ref, watch} from 'vue';
import {useKlipApiProxy} from '@/plugins/proxy-client';
import {
    klValidateRules,
    klValidationFields,
    ValidationFieldsType
} from '@/app/shared/components/kl-form-fields/kl-field-validation-util';
import * as yup from 'yup';
import {toTypedSchema} from '@vee-validate/yup';

export default defineComponent({
    compatConfig: {
        MODE: 3,
    },
    name: 'KlOrganisationUnaSettings',
    emits: ['update:modelValue'],
    props: {
        modelValue: {
            type: Object as () => IOrganisationUnaSettings,
            default: () => ({}),
            required: true,
        }
    },
    setup(props, { emit }) {

        const organisationNameValidationRegex = /^[a-zA-Z0-9\-_ .+!()&ö/:,è|'@\s]*$/;
        const schema = yup.object({
            initialDisplayName: yup.string().notRequired(),
            displayName: yup.string()
                .required()
                .max(90)
                .matches(organisationNameValidationRegex)
                .kl_organisationDisplayNameIsUnique('initialDisplayName')
                .label('Naam als leidingbeheerder in KLIP'),
            subjectSigningCertificate: yup.string().notRequired().label('Subject signing certificaat'),
        });

        const sscAnnotationLink: string = import.meta.env.VITE_LINKS_IMKL_SIGNING_HELP;
        const sscAnnotation: string = `Enkel relevant om <a href="${sscAnnotationLink}" target="_blank" class="vl-link">IMKL antwoorden digitaal te ondertekenen</a>`;
        const imklInfoUrl: string = import.meta.env.VITE_LINKS_IMKLINFO;

        const form = useForm({
            validationSchema: toTypedSchema(schema),
        });
        const fields: ValidationFieldsType<typeof schema> = klValidationFields<typeof schema>(schema);

        const disableSubmit = computed((): boolean => {
            return !form.meta.value.valid || !form.meta.value.dirty;
        });

        const iniitalDisplayName = fields.initialDisplayName.getValue<string>();
        const displayName = fields.displayName.getValue<string>();
        const subjectSigningCertificate = fields.subjectSigningCertificate.getValue<string>();

        const isSending = ref<boolean>(false);
        const isUna = computed(() => {
            return props.modelValue?.isUna;
        });

        const shouldConfirmUnaCreation = ref<boolean>(!isUna.value);

        const editable = computed(() => {
            return useUserStore().hasRole([EUserRole.una_manager, EUserRole.klim_admin]);
        });


        const confirmUnaCreation = () => {
            _saveUnaSettings();
        }


        const _saveUnaSettings = () => {
            if (shouldConfirmUnaCreation.value) {
                useSignalrHub().subToEvent(EEventType.UtilityNetworkAuthorityRegisteredEvent, _unaCreated);
                useSignalrHub().subscribeToOrganisationNotifications(props.modelValue.organisationId);
            }

            isSending.value = true;

            const organisationUnaSettingsInput: IOrganisationUnaSettingsInput = {
                organisationId: props.modelValue.organisationId,
                displayName: displayName.value,
                subjectSigningCertificate: subjectSigningCertificate.value,
            }

            useKlipApiProxy().organisation_EditUnaSettingsForOrganisation(OrganisationUnaSettingsInput.fromJS(organisationUnaSettingsInput))
                .finally(() => {
                    // wait emitting event when new una
                    if (shouldConfirmUnaCreation.value) {
                        return;
                    }

                    _emitUpdate();
                });
        }

        const _unaCreated = (message: IMessage = null) => {
            if (message && message.info === props.modelValue.organisationId) {
                _emitUpdate();
            }
        }

        const _emitUpdate = () => {
            isSending.value = false;
            shouldConfirmUnaCreation.value = false;

            const organisationUnaSettings: IOrganisationUnaSettings = {
                organisationId: props.modelValue.organisationId,
                isUna: props.modelValue.isUna,

                displayName: displayName.value,
                subjectSigningCertificate: subjectSigningCertificate.value,
            }

            emit('update:modelValue', organisationUnaSettings);

            form.resetForm({ values: form.values });
        }


        onBeforeUnmount(() => {
            useSignalrHub().unsubFromEvent(EEventType.UtilityNetworkAuthorityRegisteredEvent);
            useSignalrHub().unSubscribeFromOrganisationNotifications(props.modelValue.organisationId);
        });

        watch(
            () => props.modelValue,
            (newValue: IOrganisationUnaSettings) => {

                iniitalDisplayName.value = newValue?.displayName;
                displayName.value = newValue?.displayName;
                subjectSigningCertificate.value = newValue?.subjectSigningCertificate;

                form.resetForm({ values: form.values });
            },
            { immediate: true, deep: true }
        )


        const _onInvalidSubmit = (submittedValues) => {
            console.log('_onInvalidSubmit', submittedValues);
        }

        const onSubmit = form.handleSubmit(_saveUnaSettings, _onInvalidSubmit);

        return {
            displayName,
            subjectSigningCertificate,

            sscAnnotation,
            imklInfoUrl,

            isSending,
            shouldConfirmUnaCreation,
            isUna,

            editable,

            confirmUnaCreation,

            onSubmit,
            disableSubmit,

            fields,
        }
    }
})
